// @ts-strict-ignore
// Porting react-router's hook API to consumer API
import React, {Fragment, useCallback, useEffect, useLayoutEffect, useRef} from 'react';
import {Location, Params, To, useLocation, useNavigate, useParams} from 'react-router-dom';
import isEqual from 'lodash/isEqual';

import type {ConsumerLike} from 'common/util/react';
import {getLocation} from 'common/util/url';

export const ParamsConsumer: ConsumerLike<Readonly<Params<string>>> = ({children}) => {
  const params = useParams();
  return children(params);
};

const {origin} = getLocation();

/**
 * Navigate consumer modifies the original navigate function to keep search params
 * upon calling `navigate` in components.
 * example:
 * current url: `/project/1/view/1/insights?bar=baz`
 * this.props.navigate('/project/2/view/2/insights#foo')
 * navigates to /project/2/view/2/insights?bar=baz#foo
 */
export const useNavigateWithSearchParams = () => {
  const navigate = useNavigate();
  const {search, pathname, hash} = useLocation();
  const historyNavigate = useCallback(
    (to: To | number, options = {}) => {
      if (typeof to === `string`) {
        const {
          pathname: newPathname,
          search: newSearch,
          hash: newHash,
        } = new URL(to, `${origin}${pathname}${search}${hash}`);
        navigate(
          {
            search: newSearch,
            hash: newHash,
            pathname: newPathname,
          },
          options,
        );
      } else if (typeof to === `number`) {
        navigate(to);
      } else {
        navigate(
          {
            search,
            ...to,
          },
          options,
        );
      }
    },
    [navigate, pathname, search, hash],
  );

  return historyNavigate;
};

/**
 * Navigate consumer modifies the original navigate function to keep search params
 * upon calling `navigate` in components. It also by default only modifies the hash
 * example:
 * current url: `/project/1/view/1/insights?bar=baz`
 * this.props.navigate('foo')
 * navigates to /project/1/view/1/insights?bar=baz#foo
 */
export const useNavigateHash = () => {
  const navigate = useNavigate();
  const {search, pathname} = useLocation();
  const hashNavigate = useCallback(
    (to: To | number, options = {}) => {
      if (typeof to === `string`) {
        navigate(`${pathname}${search}#${to}`, options);
      } else if (typeof to === `number`) {
        navigate(to);
      } else {
        navigate(
          {
            search,
            ...to,
          },
          options,
        );
      }
    },
    [navigate, search, pathname],
  );

  return hashNavigate;
};

// route-matching adapted from Panel:
// https://github.com/mixpanel/panel/blob/9d3fe274ed4d52303c53ace06ac4a48174219084/lib/router.js#L21-L45
// which originally adapted it from Backbone:
// https://github.com/jashkenas/backbone/blob/d682061a/backbone.js#L1476-L1479
const optionalParam = /\((.*?)\)/g;
const namedParam = /(\(\?)?:\w+/g;
const splatParam = /\*\w+/g;
const escapeRegExp = /[\-{}\[\]+?.,\\\^$|#\s]/g; // eslint-disable-line no-useless-escape

const useBackboneHashMatch = (routes: Record<string, (...args: string[]) => void>) => {
  const location = useLocation();
  const mountedRef = useRef(null);
  const compiledRoutes = Object.entries(routes).map(([path, handler]) => {
    const cleanedUpPath = path
      .replace(escapeRegExp, `\\$&`)
      .replace(optionalParam, `(?:$1)?`)
      .replace(namedParam, (match, optional) => (optional ? match : `([^/?]+)`))
      .replace(splatParam, `([^?]*?)`);
    return [cleanedUpPath, handler] as const;
  });
  useEffect(() => {
    for (const [path, handler] of compiledRoutes) {
      const regex = new RegExp(`^` + path + `(?:\\?([\\s\\S]*))?$`);
      const matches = regex.exec(location.hash.replace(/^#/, ``));
      if (!matches) {
        continue;
      }
      if (matches && mountedRef.current !== location.hash) {
        mountedRef.current = location.hash;
        const params = matches.slice(1).map((param, i, originalParams) => {
          // Don't decode the search params.
          if (i === originalParams.length - 1) {
            return param || null;
          }
          return param ? decodeURIComponent(param) : null;
        });
        handler.call(null, ...params);
        break;
      }
    }
  }, [location, compiledRoutes, mountedRef]);
};

// eslint-disable-next-line react/display-name
export const BackboneStyleRoutes = React.memo(
  ({routes}: {routes: Record<string, (...args: string[]) => any>}) => {
    useBackboneHashMatch(routes);
    return React.createElement(Fragment);
  },
  (prevProps, curProps) => isEqual(prevProps.routes, curProps.routes),
);

export function getQueryStringParam(location: Location, paramName: string) {
  let paramVal = new URLSearchParams(location.search).get(paramName);
  if (paramVal === null) {
    paramVal = new URLSearchParams(location.hash).get(paramName); // wtf dashboards
  }
  return paramVal;
}

/**
 * React Hook for redirecting hash based route -> non-hash based route
 * this hook use `useLayoutEffect` because we want it to be run BEFORE the DOM is repainted
 * > useLayoutEffect is a version of useEffect that fires before the browser repaints the screen.
 * changing location is a DOM operation
 *
 * /project/3/view/75/app/#/edit/123 -> project/3/view/75/app/edit/123
 * /project/3/view/75/app#/edit/123 -> project/3/view/75/app/edit/123
 * /project/3/view/75/app#edit/123 -> project/3/view/75/app/edit/123
 */
export function useRedirectHashRoutes() {
  const location = useLocation();
  const navigate = useNavigate();
  useLayoutEffect(() => {
    if (location.hash) {
      if (location.pathname.endsWith(`/`) && location.hash.startsWith(`#/`)) {
        navigate(`${location.pathname}${location.hash.slice(2)}`);
      } else if (!location.pathname.endsWith(`/`) && location.hash.startsWith(`#/`)) {
        navigate(`${location.pathname}${location.hash.slice(1)}`);
      } else if (!location.pathname.endsWith(`/`) && !location.hash.startsWith(`#/`)) {
        navigate(`${location.pathname}/${location.hash.slice(1)}`);
      }
    }
  }, [location, navigate]);
}
